import React, { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  Field, MessageBar, MessageBarBody, Spinner, Text, Textarea,
} from '@fluentui/react-components';
import type { CreateErrorReportRequest, ErrorDiagnostic } from '../api';

interface Props {
  open: boolean;
  diagnostic?: ErrorDiagnostic;
  onClose: () => void;
  onSubmit: (request: CreateErrorReportRequest) => Promise<{ id: string }>;
}

const ErrorReportDialog: React.FC<Props> = ({ open, diagnostic, onClose, onSubmit }) => {
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (open) { setResult(undefined); setError(undefined); }
  }, [open]);

  const submit = async () => {
    if (description.trim().length < 10) return;
    setSubmitting(true); setError(undefined);
    try {
      const response = await onSubmit({ ...diagnostic, description: description.trim(), reproductionSteps: steps.trim() || undefined });
      setResult(response.id); setDescription(''); setSteps('');
    } catch {
      setError('The report could not be submitted. Please try again later.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogContent>
            {result ? (
              <MessageBar intent="success"><MessageBarBody>Report submitted. Reference: {result}</MessageBarBody></MessageBar>
            ) : (
              <>
                <Text block>Describe what happened without including personal, confidential, or credential information.</Text>
                <Field label="What happened?" required validationMessage={description.length > 0 && description.trim().length < 10 ? 'Enter at least 10 characters.' : undefined}>
                  <Textarea value={description} maxLength={4000} rows={4} onChange={(_, data) => setDescription(data.value)} />
                </Field>
                <Field label="Steps to reproduce (optional)">
                  <Textarea value={steps} maxLength={4000} rows={3} onChange={(_, data) => setSteps(data.value)} />
                </Field>
                <Text size={200} block>
                  Technical details sent for a captured failure: error name and message, JavaScript and component stacks, safe network method/path/status, support IDs, current path, app version, and browser type. Query strings, URL fragments, headers, request or response bodies, screenshots, and browser storage are not sent. Technical details are kept 30 days; the report is kept 90 days.
                </Text>
                {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
            {!result && <Button appearance="primary" disabled={submitting || description.trim().length < 10} onClick={submit}>{submitting ? <Spinner size="tiny" /> : 'Submit report'}</Button>}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ErrorReportDialog;